import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import AppendixPageLayout from '../components/AppendixPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'reference', label: 'ISO 3166 codes' },
];

/* ISO 3166 alpha-2 — verbatim from dev.ecomm365.com/ecommerce/Appendix-C.html */
const COUNTRIES = [
  ['Afghanistan', 'AF'], ['Albania', 'AL'], ['Algeria', 'DZ'], ['Andorra', 'AD'], ['Angola', 'AO'],
  ['Antigua and Barbuda', 'AG'], ['Argentina', 'AR'], ['Armenia', 'AM'], ['Australia', 'AU'], ['Austria', 'AT'],
  ['Azerbaijan', 'AZ'], ['Bahamas, The', 'BS'], ['Bahrain', 'BH'], ['Bangladesh', 'BD'], ['Barbados', 'BB'],
  ['Belarus', 'BY'], ['Belgium', 'BE'], ['Belize', 'BZ'], ['Benin', 'BJ'], ['Bhutan', 'BT'],
  ['Bolivia', 'BO'], ['Bosnia and Herzegovina', 'BA'], ['Botswana', 'BW'], ['Brazil', 'BR'], ['Brunei', 'BN'],
  ['Bulgaria', 'BG'], ['Burkina Faso', 'BF'], ['Burundi', 'BI'], ['Cambodia', 'KH'], ['Cameroon', 'CM'],
  ['Canada', 'CA'], ['Cape Verde', 'CV'], ['Central African Republic', 'CF'], ['Chad', 'TD'], ['Chile', 'CL'],
  ['China, Peoples Republic of', 'CN'], ['Colombia', 'CO'], ['Comoros', 'KM'], ['Congo (Congo – Kinshasa)', 'CD'],
  ['Congo (Congo – Brazzaville)', 'CG'], ['Costa Rica', 'CR'], ["Cote d'Ivoire (Ivory Coast)", 'CI'],
  ['Croatia', 'HR'], ['Cuba', 'CU'], ['Cyprus', 'CY'], ['Czech Republic', 'CZ'], ['Denmark', 'DK'],
  ['Djibouti', 'DJ'], ['Dominica', 'DM'], ['Dominican Republic', 'DO'], ['Ecuador', 'EC'], ['Egypt', 'EG'],
  ['El Salvador', 'SV'], ['Equatorial Guinea', 'GQ'], ['Eritrea', 'ER'], ['Estonia', 'EE'], ['Ethiopia', 'ET'],
  ['Fiji', 'FJ'], ['Finland', 'FI'], ['France', 'FR'], ['Gabon', 'GA'], ['Gambia, The', 'GM'],
  ['Georgia', 'GE'], ['Germany', 'DE'], ['Ghana', 'GH'], ['Greece', 'GR'], ['Grenada', 'GD'],
  ['Guatemala', 'GT'], ['Guinea', 'GN'], ['Guinea-Bissau', 'GW'], ['Guyana', 'GY'], ['Haiti', 'HT'],
  ['Honduras', 'HN'], ['Hungary', 'HU'], ['Iceland', 'IS'], ['India', 'IN'], ['Indonesia', 'ID'],
  ['Iran', 'IR'], ['Iraq', 'IQ'], ['Ireland', 'IE'], ['Israel', 'IL'], ['Italy', 'IT'],
  ['Jamaica', 'JM'], ['Japan', 'JP'], ['Jordan', 'JO'], ['Kazakhstan', 'KZ'], ['Kenya', 'KE'],
  ['Kiribati', 'KI'], ['Korea, North', 'KP'], ['Korea, South', 'KR'], ['Kuwait', 'KW'], ['Kyrgyzstan', 'KG'],
  ['Laos', 'LA'], ['Latvia', 'LV'], ['Lebanon', 'LB'], ['Lesotho', 'LS'], ['Liberia', 'LR'],
  ['Libya', 'LY'], ['Liechtenstein', 'LI'], ['Lithuania', 'LT'], ['Luxembourg', 'LU'], ['Macedonia', 'MK'],
  ['Madagascar', 'MG'], ['Malawi', 'MW'], ['Malaysia', 'MY'], ['Maldives', 'MV'], ['Mali', 'ML'],
  ['Malta', 'MT'], ['Marshall Islands', 'MH'], ['Mauritania', 'MR'], ['Mauritius', 'MU'], ['Mexico', 'MX'],
  ['Micronesia', 'FM'], ['Moldova', 'MD'], ['Monaco', 'MC'], ['Mongolia', 'MN'], ['Montenegro', 'ME'],
  ['Morocco', 'MA'], ['Mozambique', 'MZ'], ['Myanmar (Burma)', 'MM'], ['Namibia', 'NA'], ['Nauru', 'NR'],
  ['Nepal', 'NP'], ['Netherlands', 'NL'], ['New Zealand', 'NZ'], ['Nicaragua', 'NI'], ['Niger', 'NE'],
  ['Nigeria', 'NG'], ['Norway', 'NO'], ['Oman', 'OM'], ['Pakistan', 'PK'], ['Palau', 'PW'],
  ['Panama', 'PA'], ['Papua New Guinea', 'PG'], ['Paraguay', 'PY'], ['Peru', 'PE'], ['Philippines', 'PH'],
  ['Poland', 'PL'], ['Portugal', 'PT'], ['Qatar', 'QA'], ['Romania', 'RO'], ['Russia', 'RU'],
  ['Rwanda', 'RW'], ['Saint Kitts and Nevis', 'KN'], ['Saint Lucia', 'LC'], ['Saint Vincent and the Grenadines', 'VC'],
  ['Samoa', 'WS'], ['San Marino', 'SM'], ['Sao Tome and Principe', 'ST'], ['Saudi Arabia', 'SA'],
  ['Senegal', 'SN'], ['Serbia', 'RS'], ['Seychelles', 'SC'], ['Sierra Leone', 'SL'], ['Singapore', 'SG'],
  ['Slovakia', 'SK'], ['Slovenia', 'SI'], ['Solomon Islands', 'SB'], ['Somalia', 'SO'], ['South Africa', 'ZA'],
  ['Spain', 'ES'], ['Sri Lanka', 'LK'], ['Sudan', 'SD'], ['Suriname', 'SR'], ['Swaziland', 'SZ'],
  ['Sweden', 'SE'], ['Switzerland', 'CH'], ['Syria', 'SY'], ['Tajikistan', 'TJ'], ['Tanzania', 'TZ'],
  ['Thailand', 'TH'], ['Timor-Leste (East Timor)', 'TL'], ['Togo', 'TG'], ['Tonga', 'TO'], ['Trinidad and Tobago', 'TT'],
  ['Tunisia', 'TN'], ['Turkey', 'TR'], ['Turkmenistan', 'TM'], ['Tuvalu', 'TV'], ['Uganda', 'UG'],
  ['Ukraine', 'UA'], ['United Arab Emirates', 'AE'], ['United Kingdom', 'GB'], ['United States', 'US'],
  ['Uruguay', 'UY'], ['Uzbekistan', 'UZ'], ['Vanuatu', 'VU'], ['Vatican City', 'VA'], ['Venezuela', 'VE'],
  ['Vietnam', 'VN'], ['Yemen', 'YE'], ['Zambia', 'ZM'], ['Zimbabwe', 'ZW'],
];

const CountryCodes = () => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter(([name, code]) => name.toLowerCase().includes(s) || code.toLowerCase().includes(s));
  }, [q]);

  return (
    <AppendixPageLayout
      currentId="country-codes"
      title="Country Codes"
      subtitle="Below, please find the available ISO 3166 country codes used when integrating to our services."
      toc={TOC}
      mainTestId="appendix-country-codes-page"
      sidebarTestId="appendix-country-codes-sidebar"
    >
      <section id="overview" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Overview</h2>
        <p className="text-gray-700 leading-relaxed">
          All country references in the eCOMM API (billing address, shipping address, issuing-bank country) use ISO 3166-1 alpha-2 codes — a 2-letter uppercase abbreviation. Send the code, not the name.
        </p>
      </section>

      <section id="reference" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="text-2xl font-bold text-gray-900">ISO 3166 codes</h2>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by country or code…"
              className="pl-8 pr-3 py-2 w-72 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79]"
              data-testid="country-codes-filter"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Country</th>
                <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider w-36">Country Code</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(([name, code]) => (
                <tr key={name + code} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="p-3 text-sm text-gray-700 align-top">{name}</td>
                  <td className="p-3 font-mono text-sm font-bold text-emerald-700 align-top">{code}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={2} className="p-6 text-center text-sm text-gray-500">No countries match "{q}".</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">{filtered.length} of {COUNTRIES.length} countries shown.</p>
      </section>
    </AppendixPageLayout>
  );
};

export default CountryCodes;
